"use client";

import type {
  Bundle,
  BundleInput,
  CartView,
  CheckoutSession,
  DiscountCode,
  DiscountCodeInput,
  License,
  LicenseCreateInput,
  LicenseUpdateInput,
  LoginInput,
  OrderStatus,
  Product,
  ProductVersion,
  RegisterInput,
  Review,
  ReviewInput,
  StaffSettings,
  PageCustomizationInput,
  StoreSettingsInput,
  SaleInput,
  UserSummary,
  StaffPermissions,
} from "@plexstore/types";

import { apiClient } from "@/lib/api/client";

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const authService = {
  login: (input: LoginInput) => apiClient<{ redirectTo: string }>("/api/v1/auth/login", { method: "POST", body: input }),
  register: (input: Omit<RegisterInput, "acceptTerms"> & { acceptTerms: boolean }) =>
    apiClient<{ verificationRequired: boolean; verificationSent: boolean; email: string }>("/api/v1/auth/register", { method: "POST", body: input }),
  logout: () => apiClient<void>("/api/v1/auth/logout", { method: "POST" }),
  verifyEmail: (token: string) => apiClient<{ message: string }>("/api/v1/auth/verify-email", { method: "POST", body: { token } }),
  resendVerification: (email: string) => apiClient<{ message: string }>("/api/v1/auth/resend-verification", { method: "POST", body: { email } }),
  forgotPassword: (email: string) => apiClient<{ message: string }>("/api/v1/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (token: string, password: string, confirmPassword: string) =>
    apiClient<{ message: string }>("/api/v1/auth/reset-password", { method: "POST", body: { token, password, confirmPassword } }),
  changeEmail: (newEmail: string, currentPassword: string) =>
    apiClient<{ message: string }>("/api/v1/auth/change-email", { method: "POST", body: { newEmail, currentPassword } }),
  confirmEmailChange: (token: string) => apiClient<{ email: string; message: string }>("/api/v1/auth/confirm-email-change", { method: "POST", body: { token } }),
};

// ---------------------------------------------------------------------------
// Cart & checkout
// ---------------------------------------------------------------------------
export const cartService = {
  get: (discount?: string) => apiClient<CartView>("/api/v1/cart", { searchParams: { discount } }),
  addProduct: (productId: string, opts?: { quantity?: number; planId?: string }) =>
    apiClient<CartView>(`/api/v1/cart/products/${enc(productId)}`, {
      method: "POST",
      body: { quantity: opts?.quantity ?? 1, planId: opts?.planId },
    }),
  setQuantity: (productId: string, quantity: number) =>
    apiClient<CartView>(`/api/v1/cart/products/${enc(productId)}`, { method: "PATCH", body: { quantity } }),
  removeProduct: (productId: string) => apiClient<CartView>(`/api/v1/cart/products/${enc(productId)}`, { method: "DELETE" }),
  addBundle: (bundleId: string) => apiClient<CartView>(`/api/v1/cart/bundles/${enc(bundleId)}`, { method: "POST" }),
  removeBundle: (bundleId: string) => apiClient<CartView>(`/api/v1/cart/bundles/${enc(bundleId)}`, { method: "DELETE" }),
  applyDiscount: (code: string) => apiClient<CartView>("/api/v1/cart/discount", { method: "POST", body: { code } }),
};

export const checkoutService = {
  start: (provider: CheckoutSession["provider"], discountCode: string | undefined, acceptTerms: boolean) =>
    apiClient<CheckoutSession>("/api/v1/checkout", { method: "POST", body: { provider, acceptTerms, discountCode } }),
  confirm: (input: { provider: string; snapshot: string; token?: string; sessionId?: string }) =>
    apiClient<OrderStatus>("/api/v1/checkout/confirm", { method: "POST", body: input }),
  status: (snapshotId: string) => apiClient<OrderStatus>(`/api/v1/orders/${enc(snapshotId)}/status`),
};

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------
export const reviewService = {
  create: (input: ReviewInput) => apiClient<Review>("/api/v1/reviews", { method: "POST", body: input }),
  remove: (id: string) => apiClient<void>(`/api/v1/reviews/${enc(id)}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------
export const staffProductService = {
  create: (formData: FormData) => apiClient<Product>("/api/v1/staff/products", { method: "POST", formData }),
  update: (id: string, formData: FormData) => apiClient<Product>(`/api/v1/staff/products/${enc(id)}`, { method: "PATCH", formData }),
  remove: (id: string) => apiClient<void>(`/api/v1/staff/products/${enc(id)}`, { method: "DELETE" }),
  sort: (order: string[]) => apiClient<void>("/api/v1/staff/products/sort", { method: "POST", body: { order } }),
  addVersion: (id: string, formData: FormData) => apiClient<ProductVersion>(`/api/v1/staff/products/${enc(id)}/versions`, { method: "POST", formData }),
  deleteVersion: (id: string, versionId: string) => apiClient<void>(`/api/v1/staff/products/${enc(id)}/versions/${enc(versionId)}`, { method: "DELETE" }),
};

export const staffBundleService = {
  create: (input: BundleInput) => apiClient<Bundle>("/api/v1/staff/bundles", { method: "POST", body: input }),
  update: (id: string, input: BundleInput) => apiClient<Bundle>(`/api/v1/staff/bundles/${enc(id)}`, { method: "PATCH", body: input }),
  remove: (id: string) => apiClient<void>(`/api/v1/staff/bundles/${enc(id)}`, { method: "DELETE" }),
};

export const staffDiscountService = {
  create: (input: DiscountCodeInput) => apiClient<DiscountCode>("/api/v1/staff/discount-codes", { method: "POST", body: input }),
  update: (id: string, input: DiscountCodeInput) => apiClient<DiscountCode>(`/api/v1/staff/discount-codes/${enc(id)}`, { method: "PATCH", body: input }),
  remove: (id: string) => apiClient<void>(`/api/v1/staff/discount-codes/${enc(id)}`, { method: "DELETE" }),
};

export const staffSalesService = {
  start: (input: SaleInput) => apiClient<void>("/api/v1/staff/sales", { method: "POST", body: input }),
  disable: (productId: string) => apiClient<void>(`/api/v1/staff/sales/${enc(productId)}`, { method: "DELETE" }),
  setEvent: (input: { enabled: boolean; type: string | null; message: string }) => apiClient<void>("/api/v1/staff/sales/event", { method: "PUT", body: input }),
};

export const staffLicenseService = {
  create: (input: LicenseCreateInput) => apiClient<License>("/api/v1/staff/licenses", { method: "POST", body: input }),
  update: (id: string, input: LicenseUpdateInput) => apiClient<License>(`/api/v1/staff/licenses/${enc(id)}`, { method: "PATCH", body: input }),
  remove: (id: string) => apiClient<void>(`/api/v1/staff/licenses/${enc(id)}`, { method: "DELETE" }),
};

export const licenseService = {
  resetPassword: (id: string, password: string) =>
    apiClient<{ message: string }>(`/api/v1/me/licenses/${enc(id)}/password`, { method: "POST", body: { password } }),
};

export const staffUserService = {
  toggleBan: (userId: string) => apiClient<{ banned: boolean }>(`/api/v1/staff/users/${enc(userId)}/ban`, { method: "POST" }),
  grantProduct: (userId: string, productId: string) => apiClient<unknown>(`/api/v1/staff/users/${enc(userId)}/products`, { method: "POST", body: { productId } }),
  revokeProduct: (userId: string, productId: string) => apiClient<void>(`/api/v1/staff/users/${enc(userId)}/products/${enc(productId)}`, { method: "DELETE" }),
  addSerial: (userId: string, productId: string, key: string) =>
    apiClient<void>(`/api/v1/staff/users/${enc(userId)}/serials`, { method: "POST", body: { productId, key } }),
  deleteSerial: (userId: string, serialId: string) => apiClient<void>(`/api/v1/staff/users/${enc(userId)}/serials/${enc(serialId)}`, { method: "DELETE" }),
  transfer: (userId: string, input: { targetIdentifier: string; productIds: string[]; serialIds: string[] }) =>
    apiClient<{ movedProducts: number; movedSerials: number }>(`/api/v1/staff/users/${enc(userId)}/transfer`, { method: "POST", body: input }),
  findByNonce: (nonce: string) =>
    apiClient<{ download: unknown; user?: UserSummary; invoice?: unknown }>("/api/v1/staff/downloads/find", { searchParams: { nonce } }),
};

export const staffTeamService = {
  add: (identifier: string, permissions: Record<string, boolean>) =>
    apiClient<{ user: UserSummary; permissions: StaffPermissions }>("/api/v1/staff/team", { method: "POST", body: { identifier, permissions } }),
  update: (userId: string, permissions: Record<string, boolean>) =>
    apiClient<{ user: UserSummary; permissions: StaffPermissions }>(`/api/v1/staff/team/${enc(userId)}`, { method: "PATCH", body: { permissions } }),
  remove: (userId: string) => apiClient<void>(`/api/v1/staff/team/${enc(userId)}`, { method: "DELETE" }),
};

export const staffSettingsService = {
  updateStore: (input: StoreSettingsInput) => apiClient<StaffSettings>("/api/v1/staff/settings", { method: "PUT", body: input }),
  updatePage: (input: PageCustomizationInput) => apiClient<StaffSettings>("/api/v1/staff/settings/page", { method: "PUT", body: input }),
  uploadBranding: (formData: FormData) => apiClient<{ logoPath: string; faviconPath: string }>("/api/v1/staff/settings/branding", { method: "POST", formData }),
  rotateApiKey: () => apiClient<{ apiKey: string }>("/api/v1/staff/settings/api-key/rotate", { method: "POST" }),
  testEmail: (input: Record<string, unknown>) => apiClient<{ message: string }>("/api/v1/staff/settings/test-email", { method: "POST", body: input }),
  setAntiPiracy: (enabled: boolean) => apiClient<{ enabled: boolean }>("/api/v1/staff/settings/anti-piracy", { method: "PUT", body: { enabled } }),
};
